import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import type { ExperimentalSettings } from '../data/accountTypes'
import { useGetUserSettingsQuery } from '../services/projectDataApi'

/**
 * Reads one of the current user's experimental feature flags (Settings > Experimental — see
 * EXPERIMENTAL_FLAGS in SettingsModal). The settings request is skipped while signed out, so a
 * logged-out visitor sees every flag off.
 *
 * `enabled` is false until the settings land, which is what UI that merely hides itself wants (a
 * flagged nav link should not flicker in and out). Callers that take an irreversible action when the
 * flag is off — a route that redirects, say — must wait for `loaded`, or they will bounce a
 * deep-link before the answer is known.
 */
export function useExperimentalFlag(flag: keyof ExperimentalSettings): { enabled: boolean; loaded: boolean } {
  const user = useSelector((s: RootState) => s.auth.user)
  const settingsQuery = useGetUserSettingsQuery(undefined, { skip: !user })
  return {
    enabled: settingsQuery.data?.experimental_settings[flag] === true,
    loaded: !user || settingsQuery.isSuccess || settingsQuery.isError,
  }
}
